import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'
import { createPortal } from 'react-dom'

export interface ComboFieldProps {
  id: string
  label: string
  value: string
  options: string[]
  onChange: (value: string) => void
  /**
   * Shown while the box is empty. Used for a value the record inherits rather
   * than holds — a lantern's lamp read off its fixture model (issue #59) —
   * where seeding the box would make leaving it alone indistinguishable from
   * typing the same value in.
   */
  placeholder?: string
  /** A line under the field saying where an empty box's value comes from. */
  hint?: string
}

/**
 * A free-entry text field that also offers the previously-used values as a
 * dropdown — Brand, Light type, Lamp and so on (spec §3.2 autocomplete). It
 * is the open-set counterpart to `<Select>`: the value can be anything typed,
 * not just one of the options, so the "single-minute form" still commits text
 * on every keystroke the way a plain `<input>` does.
 *
 * The dropdown is a custom listbox portalled to `<body>` (issue #4, same reason
 * `<Select>` portals) and styled to match `<Select>`'s menu — the two read as
 * one family of control. A native `<datalist>` was the earlier implementation
 * (issue #3) but its popover is the browser's own and can't take the theme, so
 * the open state never matched (issue #42).
 */
export function ComboField({
  id,
  label,
  value,
  options,
  onChange,
  placeholder,
  hint,
}: ComboFieldProps) {
  const listId = `${id}-listbox`
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const query = value.trim().toLowerCase()
  const filtered = query === ''
    ? options
    : options.filter((option) => option.toLowerCase().includes(query))

  const place = useCallback(() => {
    const el = inputRef.current
    if (el !== null) setRect(el.getBoundingClientRect())
  }, [])

  // Reposition the popover when the page scrolls or resizes underneath it.
  useEffect(() => {
    if (!open) return
    place()
    const onScroll = () => place()
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [open, place])

  useLayoutEffect(() => {
    if (open) place()
  }, [open, place])

  // Drop the pending blur timer on unmount so it never fires into a gone node.
  useEffect(() => () => {
    if (blurTimer.current !== null) clearTimeout(blurTimer.current)
  }, [])

  function show() {
    setOpen(true)
    setActiveIndex(filtered.length > 0 ? 0 : -1)
  }

  function hide() {
    setOpen(false)
    setActiveIndex(-1)
  }

  function pick(option: string) {
    onChange(option)
    hide()
    inputRef.current?.focus()
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!open) {
        show()
        return
      }
      setActiveIndex((i) => (filtered.length === 0 ? -1 : (i + 1) % filtered.length))
      return
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) return
      setActiveIndex((i) => (i <= 0 ? filtered.length - 1 : i - 1))
      return
    }

    if (event.key === 'Enter' && open && activeIndex >= 0 && filtered[activeIndex] !== undefined) {
      event.preventDefault()
      pick(filtered[activeIndex])
      return
    }

    if (event.key === 'Escape' && open) {
      event.preventDefault()
      hide()
    }
  }

  // Keep the highlighted option alive across a blur→option-click: the option's
  // onMouseDown prevents default so the input keeps focus, but guard the blur
  // with a short timer in case focus slips out another way.
  function onBlur() {
    blurTimer.current = setTimeout(hide, 120)
  }

  function onFocus() {
    if (blurTimer.current !== null) {
      clearTimeout(blurTimer.current)
      blurTimer.current = null
    }
    show()
  }

  function onOptionDown(event: MouseEvent, option: string) {
    // Stop the blur firing before the click lands.
    event.preventDefault()
    pick(option)
  }

  const menuStyle = rect === null
    ? undefined
    : {
        position: 'absolute' as const,
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      }

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <div className="combo-field" data-open={open}>
        <input
          ref={inputRef}
          autoComplete="off"
          data-1p-ignore="true"
          data-lpignore="true"
          id={id}
          className="input"
          value={value}
          placeholder={placeholder}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={
            open && activeIndex >= 0 ? `${listId}-opt-${activeIndex}` : undefined
          }
          onChange={(event) => {
            onChange(event.target.value)
            if (!open) show()
            else setActiveIndex(filtered.length > 0 ? 0 : -1)
          }}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
        />
        <svg className="combo-caret" width="14" height="14" viewBox="0 0 20 20" aria-hidden="true">
          <path
            fill="currentColor"
            d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"
          />
        </svg>
      </div>
      {hint !== undefined && (
        <p className="text-muted" style={{ margin: 'var(--space-1) 0 0', fontSize: 13 }}>
          {hint}
        </p>
      )}
      {open && filtered.length > 0 && rect !== null && typeof document !== 'undefined'
        ? createPortal(
            <ul id={listId} role="listbox" className="combo-menu" style={menuStyle}>
              {filtered.map((option, index) => (
                <li
                  key={option}
                  id={`${listId}-opt-${index}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  className="combo-option"
                  data-active={index === activeIndex}
                  onMouseDown={(event) => onOptionDown(event, option)}
                >
                  {option}
                </li>
              ))}
            </ul>,
            document.body,
          )
        : null}
    </div>
  )
}